import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography,
    Checkbox,
    Radio,
    RadioGroup,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CustomInputTextField from 'components/shared/custom_input_textfield';

const zoneOptions = ['Rural', 'Urbana'];
const frequencyOptions = ['Diária', 'Semanal', 'Quinzenal', 'Mensal', 'Outro'];
const publicOptions = [
    'Consumidores locais',
    'Turistas',
    'Comerciantes',
    'Instituições públicas',
];
const equipmentOptions = [
    'Balança',
    'Câmaras frias',
    'Banheiros',
    'Pias',
    'Bancas de exposição',
    'Outro',
];

export interface NonPermanentMarket {
    name: string;
    address: string;
    neighborhood: string;
    zone: string;
    frequency: string;
    otherFrequency: string;
    predominantPublic: string;
    numFamilyFarmProducers: number;
    numWomenFamilyFarmProducers: number;
    numFamilyFarmResellers: number;
    numWomenFamilyFarmResellers: number;
    numHandicraftSellers: number;
    numWomenHandicraftSellers: number;
    numIndustrialSellers: number;
    totalMarketValue: number;
    annualMarketValue: number;
    totalFamilyFarmingValue: number;
    equipments: string[];
    otherEquipment: string;
}

export interface Step4SimData {
    referenceYear: number;
    nonPermanentMarkets: NonPermanentMarket[];
}

interface Step4Props {
    onNext: () => void;
    onBack: () => void;
    onChange: (data: Step4SimData) => void;
    data: Step4SimData;
    disabled?: boolean;
    referenceYear: number;
}

const emptyMarket: NonPermanentMarket = {
    name: '',
    address: '',
    neighborhood: '',
    zone: '',
    frequency: '',
    otherFrequency: '',
    predominantPublic: '',
    numFamilyFarmProducers: 0,
    numWomenFamilyFarmProducers: 0,
    numFamilyFarmResellers: 0,
    numWomenFamilyFarmResellers: 0,
    numHandicraftSellers: 0,
    numWomenHandicraftSellers: 0,
    numIndustrialSellers: 0,
    totalMarketValue: 0,
    annualMarketValue: 0,
    totalFamilyFarmingValue: 0,
    equipments: [],
    otherEquipment: '',
};

const Step4: React.FC<Step4Props> = ({
    onNext,
    onBack,
    onChange,
    data,
    disabled = false,
    referenceYear,
}) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [market, setMarket] = useState<NonPermanentMarket>(emptyMarket);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Estados locais para preservar a digitação dos valores monetários
    const [annualMarketValueInput, setAnnualMarketValueInput] = useState('0.00');
    const [totalFamilyFarmingValueInput, setTotalFamilyFarmingValueInput] = useState('0.00');
    const [totalMarketValueInput, setTotalMarketValueInput] = useState('0.00');

    // Atualiza o estado global sempre que a lista de feiras mudar
    const handleMarketsChange = (markets: NonPermanentMarket[]) => {
        onChange({
            referenceYear,
            nonPermanentMarkets: markets,
        });
    };

    const handleOpenDialog = (editMarket?: NonPermanentMarket, index?: number) => {
        const marketData = editMarket ? { ...emptyMarket, ...editMarket, equipments: editMarket.equipments ?? [] } : emptyMarket;
        setMarket(marketData);

        // Inicializa os inputs monetários com os valores corretos
        setAnnualMarketValueInput((marketData.annualMarketValue / 100).toFixed(2));
        setTotalFamilyFarmingValueInput((marketData.totalFamilyFarmingValue / 100).toFixed(2));
        setTotalMarketValueInput((marketData.totalMarketValue / 100).toFixed(2));

        setErrors({});
        setEditIndex(typeof index === 'number' ? index : null);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setErrors({});
        setEditIndex(null);
    };

    const handleMarketChange = (field: keyof NonPermanentMarket, value: any) => {
        setMarket((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Função para formatar entrada monetária
    const formatMonetaryInput = (value: string): string => {
        // Remove tudo exceto números
        const cleaned = value.replace(/\D/g, '');

        // Se vazio, retorna valor atual (não reseta para 0.00)
        if (!cleaned) return '';

        // Trata como centavos - sempre divide por 100
        const num = parseInt(cleaned);
        return (num / 100).toFixed(2);
    };

    // Handlers específicos para campos monetários
    const handleAnnualMarketValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatMonetaryInput(e.target.value);
        setAnnualMarketValueInput(formatted);
        const centavos = formatted ? Math.round(parseFloat(formatted) * 100) : 0;
        handleMarketChange('annualMarketValue', centavos);
    };

    const handleTotalFamilyFarmingValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatMonetaryInput(e.target.value);
        setTotalFamilyFarmingValueInput(formatted);
        const centavos = formatted ? Math.round(parseFloat(formatted) * 100) : 0;
        handleMarketChange('totalFamilyFarmingValue', centavos);
    };

    const handleTotalMarketValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatMonetaryInput(e.target.value);
        setTotalMarketValueInput(formatted);
        const centavos = formatted ? Math.round(parseFloat(formatted) * 100) : 0;
        handleMarketChange('totalMarketValue', centavos);
    };

    const formatCurrency = (value: number | undefined | null) =>
        typeof value === 'number' && !isNaN(value)
            ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            : 'Não informado';

    const validateMarket = () => {
        const newErrors: { [key: string]: string } = {};
        if (!market.name) newErrors.name = 'Campo obrigatório';
        if (!market.address) newErrors.address = 'Campo obrigatório';
        if (!market.neighborhood) newErrors.neighborhood = 'Campo obrigatório';
        if (!market.zone) newErrors.zone = 'Campo obrigatório';
        if (!market.frequency) newErrors.frequency = 'Campo obrigatório';
        if (market.frequency === 'Outro' && !market.otherFrequency) newErrors.otherFrequency = 'Campo obrigatório';
        if (!market.predominantPublic) newErrors.predominantPublic = 'Campo obrigatório';
        if (market.numFamilyFarmProducers < 0) newErrors.numFamilyFarmProducers = 'Obrigatório';
        if (market.numWomenFamilyFarmProducers < 0) newErrors.numWomenFamilyFarmProducers = 'Obrigatório';
        if (market.numFamilyFarmResellers < 0) newErrors.numFamilyFarmResellers = 'Obrigatório';
        if (market.numWomenFamilyFarmResellers < 0) newErrors.numWomenFamilyFarmResellers = 'Obrigatório';
        if (market.numHandicraftSellers < 0) newErrors.numHandicraftSellers = 'Obrigatório';
        if (market.numWomenHandicraftSellers < 0) newErrors.numWomenHandicraftSellers = 'Obrigatório';
        if (market.numIndustrialSellers < 0) newErrors.numIndustrialSellers = 'Obrigatório';
        if (!market.totalMarketValue || market.totalMarketValue < 0) newErrors.totalMarketValue = 'Obrigatório';
        // if (!market.annualMarketValue || market.annualMarketValue < 0) newErrors.annualMarketValue = 'Obrigatório';
        if (!market.totalFamilyFarmingValue || market.totalFamilyFarmingValue < 0) newErrors.totalFamilyFarmingValue = 'Obrigatório';
        return newErrors;
    };

    const handleSaveMarket = () => {
        const validation = validateMarket();
        setErrors(validation);
        if (Object.keys(validation).length === 0) {
            const updatedMarkets = [...(data.nonPermanentMarkets || [])];
            if (editIndex !== null) {
                updatedMarkets[editIndex] = market;
            } else {
                updatedMarkets.push(market);
            }
            handleMarketsChange(updatedMarkets);
            setOpenDialog(false);
            setEditIndex(null);
        }
    };

    const handleRemoveMarket = (index: number) => {
        const updated = [...(data.nonPermanentMarkets || [])];
        updated.splice(index, 1);
        handleMarketsChange(updated);
    };

    const handleEquipmentChange = (option: string) => {
        setMarket((prev) => {
            const isChecked = prev.equipments.includes(option);
            const equipments = isChecked
                ? prev.equipments.filter((eq) => eq !== option)
                : [...prev.equipments, option];

            return {
                ...prev,
                equipments,
            };
        });
    };

    return (
        <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                        <TextField
                            label="Ano de referência do Questionário"
                            type="number"
                            value={referenceYear}
                            onChange={e => onChange({
                                ...data,
                                referenceYear: Number(e.target.value)
                            })}
                            inputProps={{ min: 1900, max: 2100 }}
                            fullWidth
                            required
                            disabled={disabled}
                            sx={{ opacity: 0.5 }}
                        />
                    </Grid>
                </Grid>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Feiras Não Permanentes cadastradas</Typography>
                    <Button variant="contained" onClick={() => handleOpenDialog()} disabled={disabled}>
                        Adicionar Feira Não Permanente
                    </Button>
                </Box>
                {(!data.nonPermanentMarkets || data.nonPermanentMarkets.length === 0) && (
                    <Typography color="text.secondary">Nenhuma feira cadastrada.</Typography>
                )}
                <Box>
                    {data.nonPermanentMarkets && data.nonPermanentMarkets.map((mkt, idx) => (
                        <Paper key={idx} sx={{ p: 2, mb: 2, position: 'relative' }}>
                            <IconButton
                                aria-label="Editar"
                                onClick={() => handleOpenDialog(mkt, idx)}
                                sx={{ position: 'absolute', top: 8, right: 48 }}
                                disabled={disabled}
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton
                                aria-label="Remover"
                                onClick={() => handleRemoveMarket(idx)}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                                disabled={disabled}
                            >
                                <DeleteIcon />
                            </IconButton>
                            <Typography variant="subtitle2"><b>{mkt.name}</b> - {mkt.neighborhood} ({mkt.zone})</Typography>
                            <Typography variant="body2">Endereço: {mkt.address}</Typography>
                            <Typography variant="body2">Frequência: {mkt.frequency}{mkt.frequency === 'Outro' && mkt.otherFrequency ? ` (${mkt.otherFrequency})` : ''}</Typography>
                            <Typography variant="body2">Público predominante: {mkt.predominantPublic}</Typography>
                            <Typography variant="body2">Comerciantes/produtores da Agricultura Familiar: {mkt.numFamilyFarmProducers}</Typography>
                            <Typography variant="body2">Mulheres comerciantes/produtoras da Agricultura Familiar: {mkt.numWomenFamilyFarmProducers}</Typography>
                            <Typography variant="body2">Comerciantes da Agricultura Familiar (Revendedores): {mkt.numFamilyFarmResellers}</Typography>
                            <Typography variant="body2">Mulheres comerciantes (revendedoras) da Agricultura Familiar: {mkt.numWomenFamilyFarmResellers}</Typography>
                            <Typography variant="body2">Comerciantes de artesanato: {mkt.numHandicraftSellers}</Typography>
                            <Typography variant="body2">Mulheres comerciantes de artesanato: {mkt.numWomenHandicraftSellers}</Typography>
                            <Typography variant="body2">Comerciantes de produtos beneficiados/industrializados: {mkt.numIndustrialSellers}</Typography>
                            <Typography variant="body2">Valor total comercializado na feira: {formatCurrency(mkt.totalMarketValue)}</Typography>
                            {/*<Typography variant="body2">Valor comercializado nas feiras do município anualmente: {formatCurrency(mkt.annualMarketValue)}</Typography>*/}
                            <Typography variant="body2">Valor total comercializado pela Agricultura Familiar: {formatCurrency(mkt.totalFamilyFarmingValue)}</Typography>
                        </Paper>
                    ))}
                </Box>
            </Paper>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>{editIndex !== null ? 'Editar Feira Não Permanente' : 'Adicionar Feira Não Permanente'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Nome da feira"
                                value={market.name}
                                onChange={e => handleMarketChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Endereço da feira"
                                value={market.address}
                                onChange={e => handleMarketChange('address', e.target.value)}
                                error={!!errors.address}
                                helperText={errors.address}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Bairro/localidade"
                                value={market.neighborhood}
                                onChange={e => handleMarketChange('neighborhood', e.target.value)}
                                error={!!errors.neighborhood}
                                helperText={errors.neighborhood}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                label="Zona"
                                value={market.zone}
                                onChange={e => handleMarketChange('zone', e.target.value)}
                                error={!!errors.zone}
                                helperText={errors.zone}
                                fullWidth
                                required
                            >
                                <MenuItem value="Rural">Rural</MenuItem>
                                <MenuItem value="Urbana">Urbana</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Typography sx={{ mb: 1 }}>A feira ocorre com frequência:</Typography>
                            <RadioGroup
                                row
                                value={market.frequency}
                                onChange={e => handleMarketChange('frequency', e.target.value)}
                            >
                                {frequencyOptions.map(option => (
                                    <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={<Radio />}
                                        label={option}
                                    />
                                ))}
                            </RadioGroup>
                            {errors.frequency && <Typography color="error">{errors.frequency}</Typography>}
                            {market.frequency === 'Outro' && (
                                <TextField
                                    label="Especifique a frequência"
                                    value={market.otherFrequency}
                                    onChange={e => handleMarketChange('otherFrequency', e.target.value)}
                                    error={!!errors.otherFrequency}
                                    helperText={errors.otherFrequency}
                                    fullWidth
                                    margin="dense"
                                    required
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                label="Qual o público predominante?"
                                value={market.predominantPublic}
                                onChange={e => handleMarketChange('predominantPublic', e.target.value)}
                                error={!!errors.predominantPublic}
                                helperText={errors.predominantPublic}
                                fullWidth
                                required
                            >
                                {publicOptions.map(option => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {/* Campos numéricos e monetários */}
                        <Grid item xs={12} md={6}>
                            <CustomInputTextField
                                label="Comerciantes/produtores da Agricultura Familiar"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={market.numFamilyFarmProducers}
                                onChange={e => handleMarketChange('numFamilyFarmProducers', Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                fullWidth
                                margin="dense"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomInputTextField
                                label="Mulheres comerciantes/produtoras da Agricultura Familiar"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={market.numWomenFamilyFarmProducers}
                                onChange={e => handleMarketChange('numWomenFamilyFarmProducers', Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                fullWidth
                                margin="dense"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomInputTextField
                                label="Comerciantes da Agricultura Familiar (Revendedores)"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={market.numFamilyFarmResellers}
                                onChange={e => handleMarketChange('numFamilyFarmResellers', Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                margin="dense"
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomInputTextField
                                label="Mulheres comerciantes (revendedoras) da Agricultura Familiar"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={market.numWomenFamilyFarmResellers}
                                onChange={e => handleMarketChange('numWomenFamilyFarmResellers', Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                margin="dense"
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomInputTextField
                                label="Comerciantes de artesanato"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={market.numHandicraftSellers}
                                onChange={e => handleMarketChange('numHandicraftSellers', Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                margin="dense"
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomInputTextField
                                label="Mulheres comerciantes de artesanato"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={market.numWomenHandicraftSellers}
                                onChange={e => handleMarketChange('numWomenHandicraftSellers', Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                margin="dense"
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomInputTextField
                                label="Quantos são comerciantes de produtos beneficiados/industrializados?"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={market.numIndustrialSellers}
                                onChange={e => handleMarketChange('numIndustrialSellers', Number(e.target.value))}
                                error={errors}
                                helperText={errors}
                                margin="dense"
                                fullWidth
                                required
                            />
                        </Grid>
                        {/*<Grid item xs={12} md={6}>
                            <TextField
                                label="Valor comercializado nas feiras do município anualmente (R$)"
                                value={annualMarketValueInput}
                                onChange={handleAnnualMarketValueChange}
                                error={!!errors.annualMarketValue}
                                helperText={errors.annualMarketValue}
                                fullWidth
                                required
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
                                placeholder="0.00"
                            />
                        </Grid>*/}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Valor total comercializado pela Agricultura Familiar (R$)"
                                value={totalFamilyFarmingValueInput}
                                onChange={handleTotalFamilyFarmingValueChange}
                                error={!!errors.totalFamilyFarmingValue}
                                helperText={errors.totalFamilyFarmingValue}
                                fullWidth
                                required
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
                                placeholder="0.00"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Valor total comercializado na feira (R$)"
                                value={totalMarketValueInput}
                                onChange={handleTotalMarketValueChange}
                                error={!!errors.totalMarketValue}
                                helperText={errors.totalMarketValue}
                                fullWidth
                                required
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
                                placeholder="0.00"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSaveMarket} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button variant="outlined" onClick={onBack} disabled={disabled}>
                    Voltar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onNext}
                    size="large"
                    disabled={disabled || !data.nonPermanentMarkets || data.nonPermanentMarkets.length === 0}
                >
                    Salvar e continuar
                </Button>
            </Box>
        </Box>
    );
};

export default Step4;